/**
 * A live session's clip with the man-of-the-match Reel's overlays drawn over it (gemini-cv-project
 * scripts/bounce_detection/motm/reel/remotion/src: Stats.tsx, Tracer.tsx), in the browser:
 *
 *   speeds  BALL SPEED and BAT SWING tiles, gold Barlow Condensed type with the Reel's ball and bat
 *           icons, no backing, top right; BALL SPEED pops at the release frame, BAT SWING at the bat
 *           frame, each on the Reel's damped spring, its number counting up over frames 3-17
 *   traces  the ball from the release in a warm glow under a white core that brightens toward the
 *           ball, a gold mark where it pitched, a white flash at the bat, and -- when it met the bat --
 *           the hit in a blue glow; each drawn up to the frame on screen
 *
 * The overlay's points are in the working copy's pixels and frames (src/live/overlays.py). The video
 * sits in a box of its own aspect ratio, so an SVG with that viewBox lies exactly on the picture, and
 * the frame on screen comes from requestVideoFrameCallback's mediaTime (currentTime where it is absent).
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type RefObject, type ReactNode } from "react";
import type { LiveOverlay, TracePoint } from "../api/live";
import { acquireWarmVideo, releaseVideoToPool } from "../lib/clipPrefetch";

const GOLD = "#f2c14e";
const SEAM = "#14110a";
const BRAND = "#3e6ae1";
const BRAND_LIGHT = "#9db7ff";

/** The Reel's tile pop: Remotion's spring (damping 11, stiffness 260, mass 0.6) at 30 fps, t in frames. */
function spring(t: number): number {
  if (t <= 0) return 0;
  let x = 0;
  let v = 0;
  const dt = 1 / (30 * 20);
  for (let i = 0; i < Math.floor(t * 20); i++) {
    const a = (-260 * (x - 1) - 11 * v) / 0.6;
    v += a * dt;
    x += v * dt;
  }
  return x;
}
const easeOutCubic = (u: number) => 1 - (1 - Math.min(1, Math.max(0, u))) ** 3;

function BallIcon() {
  return (
    <svg viewBox="0 0 64 26" className="lv-ico" aria-hidden="true">
      <path d="M43 5.2 C 30 3.6, 15 7.6, 2 18.5 C 16 10.6, 30 8.4, 42.6 9.2 Z" fill={GOLD} />
      <path d="M43.4 14.8 C 33 14.2, 23 15.4, 14.5 18.6 C 24.5 17.4, 33.5 17.6, 44.2 18.4 Z" fill={GOLD} />
      <circle cx={53} cy={11.5} r={9.5} fill={GOLD} />
      <path d="M47.6 4.4 C 51.6 8.6, 51.6 14.4, 47.6 18.6" stroke={SEAM} strokeWidth={1.3} fill="none" strokeLinecap="round" />
      <path d="M50.4 3.1 C 54.4 7.8, 54.4 15.2, 50.4 19.9" stroke={SEAM} strokeWidth={1.3} fill="none" strokeLinecap="round" />
    </svg>
  );
}

function BatIcon() {
  const a0 = (25 * Math.PI) / 180;
  const a1 = (335 * Math.PI) / 180;
  const arc = (rx: number, ry: number) => {
    const p = (a: number) => `${(32 + rx * Math.cos(a)).toFixed(2)} ${(13 - ry * Math.sin(a)).toFixed(2)}`;
    return `M${p(a0)} A ${rx} ${ry} 0 1 0 ${p(a1)}`;
  };
  return (
    <svg viewBox="0 0 64 26" className="lv-ico" aria-hidden="true">
      {([[30, 12, 1], [27.5, 10.6, 0.8], [25, 9.2, 0.6]] as const).map(([rx, ry, o]) => (
        <path key={rx} d={arc(rx, ry)} stroke={GOLD} strokeWidth={1} fill="none" opacity={o} />
      ))}
      <rect x={13} y={11.4} width={15} height={3.2} rx={1.6} fill={GOLD} />
      <rect x={26.5} y={9} width={26} height={8} rx={2.4} fill={GOLD} />
    </svg>
  );
}

function Tile({ kind, value, dt }: { kind: "ball" | "bat"; value: number; dt: number }) {
  const p = spring(dt);
  const shown = Math.round(value * easeOutCubic((dt - 3) / 14));
  const style: CSSProperties = { transform: `scale(${0.5 + 0.5 * p})`, opacity: Math.min(1, Math.max(0, dt / 3)) };
  return (
    <div className="lv-tile" style={style} data-testid={`tile-${kind}`} data-value={value}>
      {kind === "ball" ? <BallIcon /> : <BatIcon />}
      <div className="lv-tile-label">{kind === "ball" ? "BALL SPEED" : "BAT SWING"}</div>
      <div className="lv-tile-num"><span>{shown}</span><small>KPH</small></div>
    </div>
  );
}

const pts = (list: TracePoint[]) => list.map((p) => `${p[1].toFixed(1)},${p[2].toFixed(1)}`).join(" ");

/** The tracer as it stands at frame `f`. */
function Traces({ ov, f, uid }: { ov: LiveOverlay; f: number; uid: string }) {
  const w = 0.92;
  const path = ov.path.filter((p) => p[0] <= f);
  if (path.length === 0) return null;
  const hitAll = ov.contact ? ov.hit : [];
  const hit = hitAll.filter((p) => p[0] <= f);
  const start = path[0]!;
  const head = (hit.length ? hit[hit.length - 1] : path[path.length - 1])!;
  const bounce = ov.frames.bounce;
  const bat = ov.frames.bat;
  const lastFrame = (hitAll.length ? hitAll[hitAll.length - 1]! : ov.path[ov.path.length - 1]!)[0];
  const fade = f <= lastFrame ? 1 : Math.max(0, 1 - (f - lastFrame) / 4);
  const hitPts: TracePoint[] = ov.bat_px && hit.length ? [[bat ?? head[0], ov.bat_px[0], ov.bat_px[1], "track"], ...hit] : hit;
  return (
    <g data-testid="traces" data-frame={f}>
      <defs>
        <filter id={`${uid}-g4`} filterUnits="userSpaceOnUse" x={-50} y={-50} width={1380} height={820}>
          <feGaussianBlur stdDeviation={4 * w} />
        </filter>
        <linearGradient id={`${uid}-in`} gradientUnits="userSpaceOnUse" x1={start[1]} y1={start[2]} x2={path[path.length - 1]![1]} y2={path[path.length - 1]![2]}>
          <stop offset="0" stopColor="#ffffff" stopOpacity={0.35} />
          <stop offset="1" stopColor="#ffffff" stopOpacity={1} />
        </linearGradient>
        {hitPts.length > 1 && (
          <linearGradient id={`${uid}-hit`} gradientUnits="userSpaceOnUse" x1={hitPts[0]![1]} y1={hitPts[0]![2]} x2={hitPts[hitPts.length - 1]![1]} y2={hitPts[hitPts.length - 1]![2]}>
            <stop offset="0" stopColor={BRAND_LIGHT} stopOpacity={0.75} />
            <stop offset="1" stopColor="#ffffff" stopOpacity={1} />
          </linearGradient>
        )}
      </defs>
      {path.length > 1 && (
        <>
          <polyline points={pts(path)} fill="none" stroke="#ffe7a3" strokeOpacity={0.5} strokeWidth={12 * w} strokeLinecap="round" strokeLinejoin="round" filter={`url(#${uid}-g4)`} />
          <polyline points={pts(path)} fill="none" stroke={`url(#${uid}-in)`} strokeWidth={4 * w} strokeLinecap="round" strokeLinejoin="round" data-testid="ball-trace" />
        </>
      )}
      {bounce != null && ov.bounce_px && f >= bounce && (() => {
        const rb = Math.min(1, (f - bounce) / 10);
        const pb = Math.min(1, (f - bounce) / 3);
        const [cx, cy] = [ov.bounce_px[0], ov.bounce_px[1] + 2];
        return (
          <g data-testid="bounce-mark">
            <ellipse cx={cx} cy={cy} rx={22 + 52 * rb} ry={5.5 + 13 * rb} fill="none" stroke={GOLD} strokeWidth={2.5} opacity={0.9 * (1 - rb)} />
            <g transform={`translate(${cx} ${cy}) scale(${0.6 + 0.4 * pb})`}>
              <ellipse rx={24} ry={6.5} fill={GOLD} opacity={0.45} filter={`url(#${uid}-g4)`} />
              <ellipse rx={20} ry={5} fill={GOLD} opacity={0.95} />
            </g>
          </g>
        );
      })()}
      {ov.contact && bat != null && ov.bat_px && f >= bat && f < bat + 7 && (() => {
        const pf = (f - bat) / 7;
        return (
          <g>
            <circle cx={ov.bat_px[0]} cy={ov.bat_px[1]} r={14 * (1 - pf) + 2} fill="#ffffff" opacity={0.9 * (1 - pf)} filter={`url(#${uid}-g4)`} />
            <circle cx={ov.bat_px[0]} cy={ov.bat_px[1]} r={8 + 46 * pf} fill="none" stroke="#ffffff" strokeWidth={4 * (1 - pf) + 0.5} opacity={1 - pf} />
          </g>
        );
      })()}
      {hitPts.length > 1 && (
        <>
          <polyline points={pts(hitPts)} fill="none" stroke={BRAND} strokeOpacity={0.9} strokeWidth={15 * w} strokeLinecap="round" strokeLinejoin="round" filter={`url(#${uid}-g4)`} />
          <polyline points={pts(hitPts)} fill="none" stroke={`url(#${uid}-hit)`} strokeWidth={4.5 * w} strokeLinecap="round" strokeLinejoin="round" data-testid="hit-trace" />
        </>
      )}
      {fade > 0 && (
        <g opacity={fade}>
          <circle cx={head[1]} cy={head[2]} r={12 * w} fill={hit.length ? "#5b84ff" : "#fff3cf"} opacity={0.8} filter={`url(#${uid}-g4)`} />
          <circle cx={head[1]} cy={head[2]} r={5 * w} fill="#ffffff" />
        </g>
      )}
    </g>
  );
}

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number;
  cancelVideoFrameCallback?: (h: number) => void;
};

/** The frame on screen, kept current while the video plays, seeks or steps. */
function useVideoFrame(ref: RefObject<HTMLVideoElement | null>, fps: number, src: string | null): number {
  const [frame, setFrame] = useState(-1);
  useEffect(() => {
    const v = ref.current as FrameCallbackVideo | null;
    if (!v) return;
    let handle = 0;
    let raf = 0;
    let stopped = false;
    const set = (t: number) => setFrame(Math.round(t * fps));
    if (v.readyState >= 1) set(v.currentTime);
    if (v.requestVideoFrameCallback) {
      const tick = (_now: number, meta: { mediaTime: number }) => {
        if (stopped) return;
        set(meta.mediaTime);
        handle = v.requestVideoFrameCallback!(tick);
      };
      handle = v.requestVideoFrameCallback(tick);
    } else {
      const loop = () => {
        if (stopped) return;
        set(v.currentTime);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }
    const onSeek = () => set(v.currentTime);
    v.addEventListener("seeked", onSeek);
    v.addEventListener("loadeddata", onSeek);
    return () => {
      stopped = true;
      if (handle && v.cancelVideoFrameCallback) v.cancelVideoFrameCallback(handle);
      if (raf) cancelAnimationFrame(raf);
      v.removeEventListener("seeked", onSeek);
      v.removeEventListener("loadeddata", onSeek);
    };
  }, [ref, fps, src]);
  return frame;
}

export function LiveClipPlayer({ src, overlay, frameSize, fps, showSpeeds, showTraces, autoPlay = false, onEnded, label, videoRef, seek, children, timeOffsetMs = 0, description }: {
  src: string | null;
  overlay: LiveOverlay | null;
  frameSize: { width: number; height: number };
  fps: number;
  showSpeeds: boolean;
  showTraces: boolean;
  autoPlay?: boolean;
  onEnded?: () => void;
  label?: string;
  videoRef?: RefObject<HTMLVideoElement | null>;
  seek?: { seconds: number; playing: boolean; serial: number } | null;
  children?: ReactNode;
  timeOffsetMs?: number;
  description?: string;
}) {
  const descriptionId = useId();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const ref = useRef<HTMLVideoElement | null>(null);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el || !src) {
      ref.current = null;
      if (videoRef) videoRef.current = null;
      return;
    }
    const v = acquireWarmVideo(src);
    v.controls = !children;
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    if (children) v.setAttribute("controlsList", "nofullscreen");
    else v.removeAttribute("controlsList");
    v.setAttribute("aria-label", label ?? "clip");
    el.insertBefore(v, el.firstChild);
    ref.current = v;
    if (videoRef) videoRef.current = v;
    if (autoPlay && !seek) void v.play().catch(() => undefined);
    return () => {
      if (ref.current === v) ref.current = null;
      if (videoRef && videoRef.current === v) videoRef.current = null;
      v.removeAttribute("aria-describedby");
      releaseVideoToPool(v);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
  useLayoutEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.setAttribute("aria-label", label ?? "clip");
    if (description) v.setAttribute("aria-describedby", descriptionId);
    else v.removeAttribute("aria-describedby");
  }, [src, label, description, descriptionId]);
  const mediaFrame = useVideoFrame(ref, fps, src);
  const frame = mediaFrame < 0 ? mediaFrame : mediaFrame + Math.round(timeOffsetMs / 1000 * fps);
  const uid = useMemo(() => `lv${Math.random().toString(36).slice(2, 8)}`, []);
  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    if (autoPlay && !seek) void v.play().catch(() => undefined);
  }, [src, autoPlay, seek]);
  useEffect(() => {
    const v = ref.current;
    if (!v || !seek) return;
    let stopped = false;
    const resume = () => {
      if (stopped) return;
      // play() at EOF restarts a video at zero; the true endpoint must stay selected.
      if (seek.playing && !(Number.isFinite(v.duration) && v.currentTime >= v.duration - .001)) void v.play().catch(() => undefined);
      else v.pause();
    };
    const apply = () => {
      v.pause();
      const t = Math.max(0, Math.min(Number.isFinite(v.duration) ? v.duration : Infinity, seek.seconds));
      if (Math.abs(v.currentTime - t) < .01) resume();
      else { v.addEventListener("seeked", resume, { once: true }); v.currentTime = t; }
    };
    if (v.readyState >= 1) apply();
    else v.addEventListener("loadedmetadata", apply, { once: true });
    return () => { stopped = true; v.removeEventListener("loadedmetadata", apply); v.removeEventListener("seeked", resume); };
  }, [src, seek]);
  useEffect(() => {
    const v = ref.current;
    if (!v || !onEnded) return;
    // Browsers can discard the fragment end after a manual seek. Enforce the
    // clip boundary too, so seeking cannot run into the rest of the match.
    let finished = false;
    const end = () => {
      const m = /#t=([\d.]+),([\d.]+)/.exec(src ?? "");
      if (!m) return;
      const stop = Number(m[2]);
      if (v.currentTime < stop - 0.05) {
        finished = false;
        return;
      }
      const notify = !finished;
      finished = true;
      if (!v.paused) v.pause();
      if (v.currentTime > stop) v.currentTime = stop;
      if (notify) onEnded();
    };
    const ended = () => {
      if (finished) return;
      finished = true;
      onEnded();
    };
    const replay = () => {
      const m = /#t=([\d.]+),([\d.]+)/.exec(src ?? "");
      if (m && finished) { finished = false; v.currentTime = Number(m[1]); }
    };
    v.addEventListener("timeupdate", end);
    v.addEventListener("pause", end);
    v.addEventListener("ended", ended);
    v.addEventListener("play", replay);
    return () => {
      v.removeEventListener("timeupdate", end);
      v.removeEventListener("pause", end);
      v.removeEventListener("ended", ended);
      v.removeEventListener("play", replay);
    };
  }, [src, onEnded]);
  const { width, height } = frameSize;
  const sp = overlay?.speeds;
  const rel = overlay?.frames.release ?? overlay?.frames.release_drawn ?? null;
  const bat = overlay?.frames.bat ?? null;
  return (
    <div ref={boxRef} className="lv-player" style={{ aspectRatio: `${width} / ${height}` }} data-testid="live-player" data-frame={frame}>
      {description && <p className="sr-only" id={descriptionId} data-testid="delivery-summary">{description}</p>}
      {!src && <div className="lv-none">This clip has no video.</div>}
      {showTraces && overlay && frame >= 0 && (
        <svg className="lv-ov" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <Traces ov={overlay} f={frame} uid={uid} />
        </svg>
      )}
      {showSpeeds && overlay && (
        <div className="lv-tiles" data-testid="speed-tiles">
          {sp?.bowl_kmh != null && rel != null && frame >= rel ? <Tile kind="ball" value={Math.round(sp.bowl_kmh)} dt={frame - rel} /> : null}
          {sp?.bat_kmh != null && bat != null && overlay.contact && frame >= bat ? <Tile kind="bat" value={Math.round(sp.bat_kmh)} dt={frame - bat} /> : null}
        </div>
      )}
      {children}
    </div>
  );
}

/** Labelled activation controls retain native checkbox behavior and keyboard access. */
export function ViewingToggle({ checked, onChange, label, accessibleLabel, testId }: {
  checked: boolean; onChange: (checked: boolean) => void; label: string;
  accessibleLabel?: string; testId: string;
}) {
  return (
    <label className={`viewing-toggle ${checked ? "on" : ""}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={accessibleLabel ?? label} data-testid={testId} />
      <span className="viewing-toggle-mark" aria-hidden="true" />
      <span className="viewing-toggle-copy"><span>{label}</span><small aria-hidden="true">{checked ? "On" : "Off"}</small></span>
    </label>
  );
}

export function RenderOptions({ speeds, traces, onChange }: { speeds: boolean; traces: boolean; onChange: (o: { speeds: boolean; traces: boolean }) => void }) {
  return (
    <div className="lv-opts" role="group" aria-label="Rendering">
      <ViewingToggle checked={speeds} onChange={(v) => onChange({ speeds: v, traces })} label="Speeds" accessibleLabel="Show speeds" testId="opt-speeds" />
      <ViewingToggle checked={traces} onChange={(v) => onChange({ speeds, traces: v })} label="Traces" accessibleLabel="Show traces" testId="opt-traces" />
    </div>
  );
}

const RENDER_KEY = "dt.live.render";
/** The render options, kept per viewer (a convenience: the page works without storage). */
export function useRenderOptions(): [{ speeds: boolean; traces: boolean }, (o: { speeds: boolean; traces: boolean }) => void] {
  const [o, set] = useState<{ speeds: boolean; traces: boolean }>(() => {
    try {
      const raw = localStorage.getItem(RENDER_KEY);
      if (raw) return JSON.parse(raw) as { speeds: boolean; traces: boolean };
    } catch {
      /* storage blocked */
    }
    return { speeds: true, traces: true };
  });
  const save = (v: { speeds: boolean; traces: boolean }) => {
    set(v);
    try {
      localStorage.setItem(RENDER_KEY, JSON.stringify(v));
    } catch {
      /* storage blocked */
    }
  };
  return [o, save];
}
